/**
 * Asset manifest schemas for all 11 kinds + Preset.
 *
 * Each kind exports:
 * - <Kind>ManifestSchema (zod)
 * - <Kind>Manifest (type)
 * - validate<Kind>(data) -> ManifestValidationResult
 */

export type {
  AssetKind,
  ManifestValidationFailure,
  ManifestValidationResult,
  ManifestValidationSuccess,
} from "./_shared.js";
// Shared
export {
  ArtifactRefSchema,
  AssetKindSchema,
  AssetKindValues,
  BaseFrontmatterSchema,
  CommonMetadataSchema,
  FieldSchema,
  FilePrerequisiteSchema,
  UserInputSchema,
  validateManifest,
} from "./_shared.js";
export type {
  AgentComposeItem,
  AgentManifest,
  AgentTools,
  AnthropicModelAlias,
  AnthropicModelFamily,
  Binding,
} from "./agent.js";
export {
  AgentComposeItemSchema,
  AgentManifestSchema,
  AgentToolsSchema,
  ANTHROPIC_FULL_ID_PATTERN,
  ANTHROPIC_MODEL_ALIASES,
  ANTHROPIC_MODEL_FAMILIES,
  BEDROCK_MODEL_ID_PATTERN,
  BindingSchema,
  BindingValues,
  ModelIdSchema,
  modelIdSchemaForCloud,
  normalizeAnthropicModel,
  validateAgent,
  VERTEX_MODEL_ID_PATTERN,
} from "./agent.js";
export type { ConnectorManifest } from "./connector.js";
export { ConnectorManifestSchema, validateConnector } from "./connector.js";
export type { MountManifest } from "./mount.js";
export { MountManifestSchema, validateMount } from "./mount.js";
export type { SkillManifest } from "./skill.js";
// Per-kind
export { SkillManifestSchema, validateSkill } from "./skill.js";

// NOTE: FlowManifestSchema, FlowManifest, validateFlow live in @skaile/workspaces/factory-assets/connectors/flow/engine.
// Import from @skaile/workspaces/factory-assets/connectors/flow/engine directly.

export type { ContractManifest } from "./contract.js";
export { ContractManifestSchema, validateContract } from "./contract.js";
export type { McpServerManifest } from "./mcp-server.js";
export { McpServerManifestSchema, validateMcpServer } from "./mcp-server.js";
export type { PromptManifest } from "./prompt.js";
export { PromptManifestSchema, validatePrompt } from "./prompt.js";

// NOTE: KnowledgeManifestSchema, KnowledgeManifest, validateKnowledge moved to @skaile/library in 1.7.4.
// Import from @skaile/library directly.

export type { PersonaManifest } from "./persona.js";
export { PersonaManifestSchema, validatePersona } from "./persona.js";
export type { Placeholder, PresetItem, PresetManifest, PresetSource } from "./preset.js";
// Preset (composition entity, not an asset kind)
export {
  PlaceholderSchema,
  PlaceholderTypeSchema,
  PresetItemSchema,
  PresetManifestSchema,
  PresetSourceSchema,
  validatePreset,
} from "./preset.js";
export type { RulesetManifest } from "./ruleset.js";
export { RulesetManifestSchema, validateRuleset } from "./ruleset.js";

// ---------------------------------------------------------------------------
// Kind-to-validator lookup
// ---------------------------------------------------------------------------

import type { AssetKind, ManifestValidationResult } from "./_shared.js";
import { validateAgent } from "./agent.js";
import { validateConnector } from "./connector.js";
import { validateContract } from "./contract.js";
import { validateMcpServer } from "./mcp-server.js";
import { validateMount } from "./mount.js";
import { validatePersona } from "./persona.js";
import { validatePrompt } from "./prompt.js";
import { validateRuleset } from "./ruleset.js";
import { validateSkill } from "./skill.js";

/**
 * Validators for core kinds that still live in this package.
 * flow: lives in @skaile/workspaces/factory-assets/connectors/flow/engine
 * knowledge: lives in @skaile/library
 *
 * For kinds not in this map, use the IAssetKindRegistry from @skaile/plugins.
 */
const validators: Partial<Record<AssetKind, (data: unknown) => ManifestValidationResult<unknown>>> =
  {
    skill: validateSkill,
    agent: validateAgent,
    connector: validateConnector,
    mount: validateMount,
    contract: validateContract,
    prompt: validatePrompt,
    "mcp-server": validateMcpServer,
    persona: validatePersona,
    ruleset: validateRuleset,
  };

/**
 * Validate a manifest against the schema for the given asset kind.
 *
 * NOTE: flow validation lives in @skaile/workspaces/factory-assets/connectors/flow/engine. Use the
 * IAssetKindRegistry for kind-agnostic validation that includes all
 * registered providers (including extension kinds).
 */
export function validateByKind(kind: AssetKind, data: unknown): ManifestValidationResult<unknown> {
  const validator = validators[kind];
  if (!validator) {
    return { ok: false, errors: [{ path: "kind", message: `Unknown asset kind: ${kind}` }] };
  }
  return validator(data);
}
